import { FC, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { FieldValues, useForm } from "react-hook-form";
import { ParentCompanySearch } from "./ParentCompanySearch";
import { ParentCompanyTable } from "./ParentCompanyTable";
import { useSetParentCompaniesState } from "../hooks/useSetParentCompaniesState";
import { useParentCompaniesValue } from "../hooks/useParentCompaniesValue";
import { fetchQueryParentCompanies } from "../miscellaneous/fetchQueryParentCompanies";
import { usePaginationTable } from "../../common/hooks/usePaginationTable";
import { useCompanyManagementSearchValue } from "../hooks/useCompanyManagementSearchValue";
import { useSetCompanyManagementSearch } from "../hooks/useSetCompanyManagementSearch";
import { defaultCompanyManagementSearchState } from "../miscellaneous/companyManagementSearchAtom";

export const ParentCompanyTab: FC = () => {
  const setParentCompanies = useSetParentCompaniesState();
  const parentCompanies = useParentCompaniesValue();
  const [totalRecords, setTotalRecords] = useState(0);

  const searchAtomState = useCompanyManagementSearchValue();
  const setSearchAtomState = useSetCompanyManagementSearch();
  const companyTabAtom = searchAtomState.companyTab;

  const [companySearch, setCompanySearchLocal] = useState(companyTabAtom.criteria.companyName);
  const [organizationSearch, setOrganizationSearchLocal] = useState(companyTabAtom.criteria.organizationName);

  const { register, handleSubmit, reset } = useForm<FieldValues>({
    defaultValues: companyTabAtom.criteria,
  });

  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
  } = usePaginationTable(parentCompanies, companyTabAtom.page, companyTabAtom.pageSize);

  // Sync page/pageSize to atom
  useEffect(() => {
    setSearchAtomState((prev) => ({
      ...prev,
      companyTab: { ...prev.companyTab, page, pageSize: rowsPerPage },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const setCompanySearch = (val: string) => {
    setCompanySearchLocal(val);
    setSearchAtomState((prev) => ({
      ...prev,
      companyTab: { ...prev.companyTab, criteria: { ...prev.companyTab.criteria, companyName: val }, hasSearched: true },
    }));
  };

  const setOrganizationSearch = (val: string) => {
    setOrganizationSearchLocal(val);
    setSearchAtomState((prev) => ({
      ...prev,
      companyTab: { ...prev.companyTab, criteria: { ...prev.companyTab.criteria, organizationName: val }, hasSearched: true },
    }));
  };

  const handleResetAtom = () => {
    setSearchAtomState((prev) => ({
      ...prev,
      companyTab: defaultCompanyManagementSearchState.companyTab,
    }));
  };

  const mutationQueryParentCompanies = useMutation(fetchQueryParentCompanies, {
    onSuccess: (res) => {
      setParentCompanies(res.data.data);
      setTotalRecords(res.data.totalRecords);
    },
    onError: () => {},
  });

  useEffect(() => {
    const payload = {
      companyName: companySearch,
      organizationName: organizationSearch,
      pageSize: rowsPerPage,
      pageNumber: page,
    };
    mutationQueryParentCompanies.mutate(payload);
  }, [JSON.stringify(companySearch), JSON.stringify(organizationSearch), page, rowsPerPage]);

  return (
    <>
      <ParentCompanySearch
        register={register}
        handleSubmit={handleSubmit}
        reset={reset}
        setCompanySearch={setCompanySearch}
        setOrganizationSearch={setOrganizationSearch}
        setPage={setPage}
        onAtomReset={handleResetAtom}
      />
      <ParentCompanyTable
        page={page}
        totalRecords={totalRecords}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  );
};
